// src/redux/slices/cartSlice.js
import { createSlice } from "@reduxjs/toolkit";

const DEFAULT_IMAGE_URL = "/placeholder.svg";

const isNonEmptyString = (value) => typeof value === "string" && value.trim().length > 0;

const resolveImageUrl = (value, fallback = DEFAULT_IMAGE_URL) => {
  if (isNonEmptyString(value)) return value.trim();
  if (Array.isArray(value) && value.length) return resolveImageUrl(value[0], fallback);
  if (value && typeof value === "object") {
    return resolveImageUrl(
      value.url ||
        value.imageUrl ||
        value.src ||
        value.href ||
        value.link ||
        value.thumbnail ||
        value.thumb ||
        value.small ||
        value.medium ||
        value.large ||
        value.original ||
        value.sku_image,
      fallback
    );
  }
  return fallback;
};

const normalizeCartItem = (incoming) => {
  const normalized = { ...(incoming || {}) };
  const picked = resolveImageUrl(
    normalized.image || normalized.imageUrl || normalized.images,
    DEFAULT_IMAGE_URL
  );
  normalized.image = picked;
  if (!isNonEmptyString(normalized.imageUrl)) normalized.imageUrl = picked;
  return normalized;
};

// Load cart from localStorage
const loadCart = () => {
  try {
    const cartData = localStorage.getItem("cart");
    const parsed = cartData ? JSON.parse(cartData) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.map((it) => {
      let withId = it;
      if (!withId?.id) {
        if (withId?.product_id && withId?.sku_id) withId = { ...withId, id: `${withId.product_id}:${withId.sku_id}` };
        else if (withId?.product_id) withId = { ...withId, id: String(withId.product_id) };
        else withId = { ...withId, id: `${Date.now()}-${Math.random().toString(16).slice(2)}` };
      }
      return normalizeCartItem(withId);
    });
  } catch (err) {
    console.error("Error loading cart:", err);
    return [];
  }
};

// Save cart to localStorage
const saveCart = (cart) => {
  try {
    localStorage.setItem("cart", JSON.stringify(cart));
  } catch (err) {
    console.error("Error saving cart:", err);
  }
};

const initialState = {
  items: loadCart(),
  checkoutItems: [],
  checkoutSummary: null,
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const incoming = action.payload;
      const key =
        incoming?.id ||
        (incoming?.product_id && incoming?.sku_id
          ? `${incoming.product_id}:${incoming.sku_id}`
          : incoming?.product_id
            ? String(incoming.product_id)
            : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

      const item = normalizeCartItem({ ...incoming, id: key });
      const existing = state.items.find((i) => i.id === item.id);
      if (existing) {
        existing.quantity += item.quantity || 1;

        const existingImg = resolveImageUrl(existing.image, DEFAULT_IMAGE_URL);
        const incomingImg = resolveImageUrl(item.image, DEFAULT_IMAGE_URL);
        if (!isNonEmptyString(existing.image) || existingImg === DEFAULT_IMAGE_URL) {
          existing.image = incomingImg;
        }
        if (!isNonEmptyString(existing.imageUrl) || existing.imageUrl === DEFAULT_IMAGE_URL) {
          existing.imageUrl = incomingImg;
        }
      } else {
        state.items.push({ ...item, quantity: item.quantity || 1 });
      }
      saveCart(state.items);
    },
    checkout: (state, action) => {
      const payload = action.payload;

      // Backward compatible: allow passing an array directly.
      if (payload && typeof payload === "object" && !Array.isArray(payload) && Array.isArray(payload.items)) {
        state.checkoutItems = payload.items;
        state.checkoutSummary = payload.summary || null;
      } else {
        state.checkoutItems = payload;
        state.checkoutSummary = null;
      }
    },
    removeFromCart: (state, action) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
      saveCart(state.items);
    },
    clearCart: (state) => {
      state.items = [];
      saveCart(state.items);
    },
    updateQuantity: (state, action) => {
      const { id, quantity } = action.payload;
      const item = state.items.find((item) => item.id === id);
      if (item && quantity > 0) {
        item.quantity = quantity;
      }
      saveCart(state.items);
    },
  },
});

export const { addToCart, removeFromCart, clearCart, updateQuantity,checkout } =
  cartSlice.actions;
export default cartSlice.reducer;
